import { MotionDiv } from '@/components/element/MotionDiv';
import { usePurchaseOrderInvalidate } from '@/hooks/api/0570_purchase_order';
import { usePaymentTaskPhases } from '@/hooks/api/0950_payment_task_phases';
import { usePaymentTasksInvalidate } from '@/hooks/api/0960_payment_tasks';
import { usePaymentTaskInvalidate } from '@/hooks/api/1000_payment_task';
import { useMovePaymentTaskPhaseToNext } from '@/hooks/api/1010_move_to_next_phase';
import { useMovePaymentTaskPhaseToPrev } from '@/hooks/api/1011_move_to_prev_phase';
import { PaymentTaskPhase } from '@/hooks/api/models';
import { toastApiError } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentPaymentTask } from '@/providers/PaymentTaskProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { Fragment, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

export const TaskProgress = () => {
  const { _w_, _t_, me, meTeamMember } = useCurrentTeamMember();
  const { paymentTask: task } = useCurrentPaymentTask();
  const { data: phases } = usePaymentTaskPhases();
  const [currentPhase, setCurrentPhase] = useState(task!.payment_task_phase);
  const { invalidate: invalidateTasks } = usePaymentTasksInvalidate(_w_!.identifier);

  useEffect(() => {
    setCurrentPhase(task!.payment_task_phase);
    invalidateTasks();
  }, [task]);

  return (
    <MotionDiv className="card mb-4">
      <div className="card-header d-flex align-items-center justify-content-between">
        <div>
          <h6 className="mb-0">タスク進捗状況</h6>
        </div>
      </div>
      <div className="card-body pt-0">
        {phases?.map((phase, i) => (
          <Fragment key={`poctp-${phase.id}`}>
            {i != 0 && <hr className="horizontal dark" />}
            <div className="d-flex align-items-center justify-content-between">
              <div className="text-sm text-dark font-weight-bold">{phase.label}</div>
              <div className="d-flex justify-content-end align-items-center">
                {phase.id == currentPhase.id && (
                  <img src={task!.user.user_account.icon_file_url} className="avatar avatar-sm rounded-circle" />
                )}
                {phase.id < currentPhase.id && <span className="badge badge-success">OK</span>}
                <MoveToPrevPhaseButton phase={phase} curPhase={currentPhase} phases={phases} />
                <MoveToNextPhaseButton phase={phase} curPhase={currentPhase} phases={phases} />
              </div>
            </div>
          </Fragment>
        ))}
      </div>
    </MotionDiv>
  );
};

type ButtonProps = {
  phase: PaymentTaskPhase;
  curPhase: PaymentTaskPhase;
  phases: PaymentTaskPhase[];
};

// 次のフェーズボタン
const MoveToNextPhaseButton = ({ phase, curPhase, phases }: ButtonProps) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me, meTeamMember, isApprover } = useCurrentTeamMember();
  const { purchase_order_id } = useParams();
  const { paymentTask: task } = useCurrentPaymentTask();
  const { mutate, data, isPending, isSuccess, error } = useMovePaymentTaskPhaseToNext();
  const { invalidate: invalidateTask } = usePaymentTaskInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    purchase_order_id: Number(purchase_order_id),
    id: task!.id,
  });
  const { invalidate: invalidatePurchaseOrder } = usePurchaseOrderInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: Number(purchase_order_id),
  });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (isSuccess && data) {
      invalidateTask();
      invalidatePurchaseOrder();
    }
  }, [isSuccess]);

  const onClick = () => {
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: Number(purchase_order_id),
      task_id: task!.id,
      current_payment_task_phase_id: curPhase.id,
    });
  };

  if (phase.id != curPhase.id) return <></>;
  if (phase.id == phases.at(-1)!.id) return <></>;

  return (
    <button className="btn btn-icon-only bg-gradient-success ms-1" onClick={onClick}>
      <i className="fas fa-arrow-down"></i>
    </button>
  );
};

// 前のフェーズボタン
const MoveToPrevPhaseButton = ({ phase, curPhase, phases }: ButtonProps) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me, meTeamMember, isApprover } = useCurrentTeamMember();
  const { purchase_order_id } = useParams();
  const { paymentTask: task } = useCurrentPaymentTask();
  const { mutate, data, isPending, isSuccess, error } = useMovePaymentTaskPhaseToPrev();
  const { invalidate: invalidateTask } = usePaymentTaskInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    purchase_order_id: Number(purchase_order_id),
    id: task!.id,
  });
  const { invalidate: invalidatePurchaseOrder } = usePurchaseOrderInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: Number(purchase_order_id),
  });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (isSuccess && data) {
      invalidateTask();
      invalidatePurchaseOrder();
    }
  }, [isSuccess]);

  const onClick = () => {
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: Number(purchase_order_id),
      task_id: task!.id,
      current_payment_task_phase_id: curPhase.id,
    });
  };

  if (phase.id != curPhase.id) return <></>;
  if (phase.id == phases[0].id) return <></>;

  return (
    <button className="btn btn-icon-only bg-gradient-warning ms-1" onClick={onClick}>
      <i className="fas fa-arrow-up"></i>
    </button>
  );
};
